import { PAYMENT_METHOD_MAP, ATTRIBUTE_LABELS } from "../constants/EarningsPayout";

export const PAYOUT_METHODS = {
  BANK_ACH: 'bankACH',
  CRYPTO: 'cryptocurrency',
};

/**
 * Builds payer options for BANK_DEPOSIT services from non-crypto companies.
 * Returns an array of `{ label, value }` objects for use in dropdowns.
 *
 * @param {Object} listServicesState - The list services state from Redux
 * @returns {Array<{label: string, value: string}>}
 */
export const getBankDepositPayerOptions = (country, listServicesState) => {
  if (!country) {
    return [];
  }

  const companies = listServicesState?.data?.data?.companies || [];

  const eligibleCompanies = companies
    .filter(
      (company) =>
        Array.isArray(company?.services) &&
        company.services.length > 0
    )
    .map((company) => {
      const bankServices = company.services.filter(
        (service) => service?.delivery_type === PAYMENT_METHOD_MAP.BANK_DEPOSIT
      );

      const totalPayers = bankServices.reduce(
        (sum, service) => sum + (service?.payers?.length || 0),
        0
      );

      return {
        company,
        bankServices,
        totalPayers
      };
    })
    .filter((entry) => entry.totalPayers > 0);

  if (!eligibleCompanies.length) {
    return [];
  }

  const bestCompanyEntry = eligibleCompanies.reduce((best, current) =>
    current.totalPayers > best.totalPayers ? current : best
  );

  const payers = bestCompanyEntry.bankServices.flatMap(
    (service) => service?.payers || []
  );

  // Filter payers for US: only return "Next Day Bank Deposit"
  const filteredPayers = country?.toLowerCase() === 'us'
    ? payers.filter((payer) => payer?.payer_name === 'Next Day Bank Deposit')
    : payers;

  return filteredPayers.map((payer) => ({
    label: payer?.payer_name,
    value: payer?.destination_token
  }));
};

/**
 * Builds payer options for Crypto company services.
 * Returns an array of `{ label, value }` objects for use in dropdowns.
 *
 * @param {Object} listServicesState - The list services state from Redux
 * @returns {Array<{label: string, value: string}>}
 */

export const getCryptoPayerOptions = (country, listServicesState) => {
  if (!country) {
    return [];
  }

  const companies = listServicesState?.data?.data?.companies || [];

  const eligibleCompanies = companies
    .filter(
      (company) =>
        Array.isArray(company?.services) &&
        company.services.length > 0
    )
    .map((company) => {
      const cryptoServices = company.services.filter(
        (service) => service?.delivery_type === PAYMENT_METHOD_MAP.CRYPTOCURRENCY
      );

      const totalPayers = cryptoServices.reduce(
        (sum, service) => sum + (service?.payers?.length || 0),
        0
      );

      return {
        company,
        cryptoServices,
        totalPayers
      };
    })
    .filter((entry) => entry.totalPayers > 0);

  if (!eligibleCompanies.length) {
    return [];
  }

  const bestCompanyEntry = eligibleCompanies.reduce((best, current) =>
    current.totalPayers > best.totalPayers ? current : best
  );

  const payers = bestCompanyEntry.cryptoServices.flatMap(
    (service) => service?.payers || []
  );

  const filteredPayers = payers.filter(
    (payer) => payer?.payer_name !== "Bitcoin" && payer?.payer_name !== "Bitcoin Lighting"
  );

  return filteredPayers.map((payer) => ({
    label: payer?.payer_name,
    value: payer?.destination_token
  }));
};

/**
 * Payer Selection Dropdown Component
 * @param {Object} props - Component props
 * @param {string} props.id - Input ID for the select element
 * @param {string} props.label - Label text for the dropdown
 * @param {string} props.value - Current selected value
 * @param {Function} props.onChange - Change handler function
 * @param {Array} props.options - Array of {value, label} option objects
 */
export const PayerSelectionDropdown = ({ id, label, value, onChange, options }) => (
  <div className="cm_dark_form mb-3">
    <div className="w-full">
      <label htmlFor={id} className="form-label mb-1">
        {label}
      </label>
      <div className="with_down_angle">
        <select
          id={id}
          className="form-control cm_form_control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose Payer</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

/**
 * Map payout method to delivery type
 * @param {string} method - The payout method (PAYOUT_METHODS.BANK_ACH or PAYOUT_METHODS.CRYPTO)
 * @returns {string} - The delivery type (PAYMENT_METHOD_MAP.BANK_DEPOSIT or PAYMENT_METHOD_MAP.CRYPTOCURRENCY)
 */
// Map payout method to delivery type
export const deliveryTypeMap = {
  [PAYOUT_METHODS.BANK_ACH]: PAYMENT_METHOD_MAP.BANK_DEPOSIT,
  [PAYOUT_METHODS.CRYPTO]: PAYMENT_METHOD_MAP.CRYPTOCURRENCY,
};

/**
 * Gets the destination token, currency symbol, company name, and payer name for a given payout method from the list services state
 * @param {Object} listServicesState - The list services state from Redux
 * @param {string} method - The payout method (PAYOUT_METHODS.BANK_ACH or PAYOUT_METHODS.CRYPTO)
 * @param {string} payerToken - The payer token to use for the payout (required)
 * @returns {Object|null} - An object with destinationToken, currencySymbol, company_name, and payer_name, or null if not found
 */
export const getDestinationToken = (listServicesState, method, payerToken) => {
  if (!listServicesState?.data?.data?.companies || !payerToken) {
    return null;
  }

  const deliveryType = deliveryTypeMap[method];
  if (!deliveryType) {
    return null;
  }

  const companies = listServicesState.data.data.companies;

  // Find the company and payer that match
  let foundCompany = null;
  let foundPayer = null;

  for (const company of companies) {
    const services = company?.services || [];
    for (const service of services) {
      if (service?.delivery_type === deliveryType) {
        const payers = service?.payers || [];
        const payer = payers.find((p) => p?.destination_token === payerToken);
        if (payer) {
          foundCompany = company;
          foundPayer = payer;
          break;
        }
      }
    }
    if (foundPayer) {
      break;
    }
  }

  if (!foundPayer) {
    return null;
  }

  return {
    destinationToken: foundPayer.destination_token,
    currencySymbol: foundPayer.exchange_rate?.[0]?.currency_symbol || 'USD',
    company_name: foundCompany?.company_name || '',
    payer_name: foundPayer?.payer_name || '',
  };
};


const MASSPAY_LABEL_OVERRIDES = [
  {
    label: 'Your Address 1',
    displayLabel: 'Your house no',
  }
];

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DIGIT_HINT_PATTERNS = [
  'Only digits',
  /^Only \d+ digits$/,
  /^\d+ digits$/
];

/**
 * Checks if expected_value should be displayed as a hint
 * @param {string} expectedValue - The expected value string
 * @returns {boolean} Whether to show the hint
 */
const shouldShowExpectedValue = (expectedValue) => {
  if (!expectedValue?.trim()) {
    return false;
  }

  const trimmed = expectedValue.trim();
  return (
    trimmed === DIGIT_HINT_PATTERNS[0] ||
    DIGIT_HINT_PATTERNS[1].test(trimmed) ||
    DIGIT_HINT_PATTERNS[2].test(trimmed)
  );
};

/**
 * Processes label by checking MASSPAY_LABEL_OVERRIDES for displayLabel override
 * @param {string} label - The label string
 * @returns {string} The processed label (displayLabel if found, otherwise original label)
 */
const processLabel = (label) => {
  if (!label) return label;

  const matchedOption = MASSPAY_LABEL_OVERRIDES.find(
    (option) => option.label === label
  );

  return matchedOption?.displayLabel || label;
};

/**
 * Renders a form label with optional required indicator
 */
const FormLabel = ({ htmlFor, label, isRequired }) => (
  <label htmlFor={htmlFor} className="form-label mb-0">
    {label}
    {isRequired && <span className="modern-field-required">*</span>}
  </label>
);

/**
 * Renders error message for a form field
 */
const FormError = ({ error }) =>
  error ? <div className="invalid-feedback d-block">{error.message}</div> : null;

/**
 * Dynamic Form Component that renders fields based on attributes
 * @param {Object} props - Component props
 * @param {Object} props.form - React Hook Form form instance
 * @param {Array} props.attributes - Array of attribute objects from masspay API
 */
export const DynamicMasspayForm = ({ form, attributes = [] }) => {
  const {
    register,
    formState: { errors, touchedFields, isSubmitted }
  } = form;

  if (!attributes?.length) {
    return (
      <div className="mb-3">
        <p className="text-secondary">Loading form fields...</p>
      </div>
    );
  }

  const requiredAttributes = attributes.filter((attr) => !attr.is_optional);

  if (!requiredAttributes.length) {
    return (
      <div className="mb-3">
        <p className="text-secondary">No required fields to display.</p>
      </div>
    );
  }

  const renderField = (attr) => {
    const {
      token,
      label,
      input_type,
      is_optional,
      expected_value,
      validation
    } = attr;

    const error = errors[token];
    const isTouched = touchedFields[token];
    const showError = error && (isTouched || isSubmitted);
    const isRequired = !is_optional;
    const displayLabel = processLabel(label);
    const inputClassName = `form-control cm_form_control ${showError ? 'is-invalid' : ''}`.trim();

    // Options/Select field
    if (input_type === 'options') {
      const options = validation?.split('|') || [];

      return (
        <div key={token} className="mb-3">
          <FormLabel htmlFor={token} label={displayLabel} isRequired={isRequired} />
          <select
            id={token}
            {...register(token)}
            className={inputClassName}
          >
            <option value="">Select {displayLabel.toLowerCase()}</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {/* <FormError error={error} /> */}
        </div>
      );
    }

    // Date field
    if (input_type === 'date') {
      return (
        <div key={token} className="mb-3">
          <div className="d-flex justify-content-between align-items-baseline mb-1">
            <FormLabel htmlFor={token} label={displayLabel} isRequired={isRequired} />
          </div>
          <input
            type="date"
            id={token}
            placeholder="yyyy-mm-dd"
            {...register(token, {
              pattern: {
                value: DATE_PATTERN,
                message: 'Please enter a valid date in yyyy-mm-dd format'
              }
            })}
            className={inputClassName}
          />
          {/* <FormError error={error} /> */}
        </div>
      );
    }

    // Default text field
    return (
      <div key={token} className="mb-3">
        <div className="d-flex justify-content-between align-items-baseline mb-1">
          <FormLabel htmlFor={token} label={displayLabel} isRequired={isRequired} />
          {shouldShowExpectedValue(expected_value) && (
            <small className="form-text text-muted mb-0">
              {expected_value}
            </small>
          )}
        </div>
        <input
          type="text"
          id={token}
          {...register(token)}
          className={inputClassName}
          placeholder={`Enter ${displayLabel.toLowerCase()}`}
        />
        {/* <FormError error={error} /> */}
      </div>
    );
  };

  return <>{requiredAttributes.map(renderField)}</>;
};

/**
 * Extracts and formats account number or wallet address from form data
 * @param {string} paymentType - The payment type (PAYMENT_METHOD_MAP.BANK_DEPOSIT or PAYMENT_METHOD_MAP.CRYPTOCURRENCY)
 * @param {Object} masspayAttributes - The masspay attributes object containing form field definitions
 * @param {Object} formData - The form data object containing field values
 * @returns {string} - Formatted account/wallet value as "xxxx-last4chars" or empty string
 */
export const extractAndFormatAccountOrWallet = (payoutMethod, masspayAttributes, formData) => {
  // Determine payment type based on payout method
  const paymentType = payoutMethod === PAYOUT_METHODS.BANK_ACH
    ? PAYMENT_METHOD_MAP.BANK_DEPOSIT
    : payoutMethod === PAYOUT_METHODS.CRYPTO
      ? PAYMENT_METHOD_MAP.CRYPTOCURRENCY
      : null;

  let accountOrWalletValue = 'xxxx';

  const allAttributes = Array.isArray(masspayAttributes?.data)
    ? masspayAttributes.data
      .filter((attrSet) => Array.isArray(attrSet?.attributes))
      .flatMap((attrSet) => attrSet.attributes)
    : [];

  if (paymentType === PAYMENT_METHOD_MAP.BANK_DEPOSIT) {
    // Find BankAccountNumber attribute token from masspayAttributes
    const bankAccountAttr = allAttributes.find(
      (attr) => attr.label === ATTRIBUTE_LABELS.BANK_ACCOUNT_NUMBER
    );
    if (bankAccountAttr && formData[bankAccountAttr.token]) {
      accountOrWalletValue = formData[bankAccountAttr.token];
    }
  } else if (paymentType === PAYMENT_METHOD_MAP.CRYPTOCURRENCY) {
    // For cryptocurrency, find WalletAddress (usually the first attribute or one with wallet-related label)
    const walletAttr = allAttributes.find(
      (attr) => attr.label?.toLowerCase().includes('wallet') ||
        attr.label?.toLowerCase().includes('address')
    ) || allAttributes[0];
    if (walletAttr && formData[walletAttr.token]) {
      accountOrWalletValue = formData[walletAttr.token];
    }
  }

  // Format accountOrWalletValue as xxxx-last4chars format
  if (accountOrWalletValue) {
    // Get the last 4 characters (preserving original characters, not just digits)
    if (paymentType === PAYMENT_METHOD_MAP.BANK_DEPOSIT) {
      const last4Chars = accountOrWalletValue.slice(-4);
      if (last4Chars.length > 0) {
        accountOrWalletValue = `${last4Chars}`;
      }
    }
    // else if (paymentType === PAYMENT_METHOD_MAP.CRYPTOCURRENCY) {
    //   accountOrWalletValue = formatWalletAddress(accountOrWalletValue);
    // }
  }

  return accountOrWalletValue;
};

/**
 * Formats a wallet address for display by truncating the middle part.
 * Example: "abcdefghijklmnopqrstuv" -> "abcdefghij…mnopqrstuv"
 *
 * @param {string} walletAccountNumber - The full wallet address/account string
 * @param {boolean} isSmall - Whether to use a small format
 * @returns {string} - Formatted wallet address
 */
export const formatWalletAddress = (walletAccountNumber = '', isSmall = false) => {
  if (!walletAccountNumber) return '';

  return walletAccountNumber.length >= 19
    ? isSmall ?
      `${walletAccountNumber.slice(0, 5)}…${walletAccountNumber.slice(-5)}` :
      `${walletAccountNumber.slice(0, 10)}…${walletAccountNumber.slice(-9)}`
    : walletAccountNumber;
};